import { Injectable } from '@angular/core';
import { IconoEstado, IEstado } from 'modelos/src';
import { LoginService } from '../modulos/login/login.service';

/**
 * Definición de un ícono de estado.
 *
 * `hex` es el color del círculo del asset (sampleado del propio PNG). Lo usa el mapa
 * para tintar el pin SDF y para colorear los clusters; el listado/detalle usan el
 * asset directo.
 */
export interface IconoEstadoDef {
  label: string;
  asset: string;
  hex: string;
}

/**
 * Catálogo de íconos de estado.
 *
 * Los slugs son la fuente de verdad y viven en gas-modelos (`IconoEstadoSchema`).
 * Este catálogo es un VALOR y por eso está duplicado con gas-web-admin (que lo usa
 * para el picker): modelos declara los slugs, cada frontend resuelve cómo se dibujan.
 * El `Record<IconoEstado, ...>` hace fallar el build si modelos agrega un slug.
 *
 * ⚠️ NO renombrar ni borrar archivos de `assets/estados/`. La app corre detrás de un
 * service worker: los clientes con el bundle anterior cacheado siguen pidiendo los
 * nombres viejos, y si el archivo ya no está en la imagen nueva les queda el ícono
 * roto hasta que el SW se actualice — lo que en las pantallas 24/7, que nunca
 * recargan, no pasa nunca. Ya ocurrió: v4.11.1 renombró `Dado de Baja.png` a
 * `dado-de-baja.png` y borró `sin-dispositivo.png`, y los dos volvieron como copias
 * de compatibilidad (no referenciadas desde el código).
 */
export const CATALOGO_ICONOS_ESTADO: Record<IconoEstado, IconoEstadoDef> = {
  ok: {
    label: 'Verde brillante — tilde',
    asset: 'assets/estados/ok.png',
    hex: '#02DA3D',
  },
  incompleto: {
    label: 'Verde oscuro — tilde con documento',
    asset: 'assets/estados/incompleto.png',
    hex: '#168526',
  },
  'sin-reportar': {
    label: 'Rojo — signo de admiración',
    asset: 'assets/estados/sin-reportar.png',
    hex: '#FF0000',
  },
  resolver: {
    label: 'Naranja — signo de admiración',
    asset: 'assets/estados/resolver.png',
    hex: '#F7931D',
  },
  'sin-asignar': {
    label: 'Violeta — signo de admiración',
    asset: 'assets/estados/sin-asignar.png',
    hex: '#7A1996',
  },
  alerta: {
    label: 'Amarillo — triángulo de advertencia',
    asset: 'assets/estados/alerta.png',
    hex: '#FDED22',
  },
  mantenimiento: {
    label: 'Celeste — herramientas',
    asset: 'assets/estados/mantenimiento.png',
    hex: '#00B2D0',
  },
  'dado-de-baja': {
    label: 'Negro — círculo tachado',
    asset: 'assets/estados/dado-de-baja.png',
    hex: '#2C2C2C',
  },
  'sin-informacion': {
    label: 'Rojo oscuro — cruz',
    asset: 'assets/estados/sin-informacion.webp',
    hex: '#D0091D',
  },
};

/**
 * Asignación por defecto estado → ícono. Es la que estaba hardcodeada en cada
 * consumidor antes de este cambio, así que un cliente sin `config.iconosEstado` ve
 * exactamente lo mismo que antes.
 */
export const ICONOS_ESTADO_DEFAULT: Record<IEstado, IconoEstado> = {
  Operativa: 'ok',
  Incompleto: 'incompleto',
  'Sin Reportar': 'sin-reportar',
  // Alias visual de "Sin Reportar": comparten ícono a propósito.
  'Sin Comunicación': 'sin-reportar',
  Resolver: 'resolver',
  'En Mantenimiento': 'mantenimiento',
  Alerta: 'alerta',
  'Sin Asignar': 'sin-asignar',
  'Dado de Baja': 'dado-de-baja',
};

/** Ícono para un punto sin estado o con un estado que no está en el catálogo. */
export const ICONO_ESTADO_DESCONOCIDO: IconoEstado = 'sin-informacion';

@Injectable({ providedIn: 'root' })
export class IconosEstadoService {
  /**
   * Ícono de un estado: el que configuró el cliente, o el default.
   *
   * "Sin Comunicación" hereda de "Sin Reportar" si no tiene una entrada propia — es
   * su alias visual en todo el frontend y así un cliente que sólo configura
   * "Sin Reportar" no queda con los dos estados de colores distintos.
   */
  public slug(estado?: IEstado): IconoEstado {
    if (!estado) return ICONO_ESTADO_DESCONOCIDO;
    const configurados =
      LoginService.getUsuario()?.cliente?.config?.iconosEstado;
    const propio = configurados?.[estado];
    if (propio && CATALOGO_ICONOS_ESTADO[propio]) return propio;
    if (estado === 'Sin Comunicación') {
      const heredado = configurados?.['Sin Reportar'];
      if (heredado && CATALOGO_ICONOS_ESTADO[heredado]) return heredado;
    }
    return ICONOS_ESTADO_DEFAULT[estado] ?? ICONO_ESTADO_DESCONOCIDO;
  }

  public def(estado?: IEstado): IconoEstadoDef {
    return CATALOGO_ICONOS_ESTADO[this.slug(estado)];
  }

  /** Path del PNG/WEBP. Lo consumen listados, detalle, popups y el sidebar del mapa. */
  public asset(estado?: IEstado): string {
    return this.def(estado).asset;
  }

  /** Color del círculo. Lo consume el mapa (pin SDF tintado + clusters). */
  public hex(estado?: IEstado): string {
    return this.def(estado).hex;
  }

  /**
   * Color de texto legible sobre el color del estado, por luminancia.
   *
   * Reemplaza la tabla blanco/negro que estaba hardcodeada estado por estado en el
   * cluster del mapa. Verificado: para los 8 colores del catálogo devuelve
   * exactamente lo que decía esa tabla, así que no hay cambio visual — y ante un
   * color reasignado sigue dando el contraste correcto en lugar de heredar el del
   * estado que ocupaba ese lugar.
   */
  public colorTexto(estado?: IEstado): string {
    return IconosEstadoService.colorTextoSobre(this.hex(estado));
  }

  public static colorTextoSobre(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const brillo = (r * 299 + g * 587 + b * 114) / 1000;
    return brillo > 125 ? '#000000' : '#FFFFFF';
  }
}
